// @ts-strict-ignore
import {ChartTypeDisplayName, ChartTypeTooltip, ChartVizType} from 'common/report/constants';
import {ChartType} from 'common/types/reports/bookmark';
import type {SectionItem, TooltipInfo} from 'common/components/items-menu/types';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import barSvg from 'common/assets/images/charts/bar.svg';
import lineSvg from 'common/assets/images/charts/line.svg';
import metricSvg from 'common/assets/images/charts/metric.svg';
import pieSvg from 'common/assets/images/charts/pie.svg';
import stackedBarSvg from 'common/assets/images/charts/stacked-bar.svg';
import stackedLineSvg from 'common/assets/images/charts/stacked-line.svg';
import stackedColumnSvg from 'common/assets/images/charts/stacked-column.svg';
import tableSvg from 'common/assets/images/charts/table.svg';
import funnelFrequencySvg from 'common/assets/images/charts/funnel-frequency.svg';
import funnelStepsSvg from 'common/assets/images/charts/funnel-steps.svg';
import funnelTtcSvg from 'common/assets/images/charts/funnel-ttc.svg';
import funnelTrendsSvg from 'common/assets/images/charts/funnel-trends.svg';
import sankeySvg from 'common/assets/images/charts/sankey.svg';
import retentionTableSvg from 'common/assets/images/charts/retention-table.svg';
import retentionTrendsSvg from 'common/assets/images/charts/retention-trends.svg';
import columnSVG from 'common/assets/images/charts/column.svg';

const commonTooltipAttrs: TooltipInfo = {
  size: DisplaySize.Small,
  placement: Placement.Right,
};

export const CHART_VIZ_TYPE_ICONS: {[key in ChartVizType]: string} = {
  [ChartVizType.Bar]: `bar`,
  [ChartVizType.Line]: `line`,
  [ChartVizType.Metric]: `single-metric`,
  [ChartVizType.Steps]: `funnel-steps`,
  [ChartVizType.Sankey]: `top-paths`,
};

export type ChartGroup = `series` | `bar` | `standard` | `special`;
export const ChartTypeSelection: Partial<{[key in ChartType]: SectionItem<ChartType, {chartGroup: ChartGroup}>}> = {
  [ChartType.Bar]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.Bar],
    value: ChartType.Bar,
    tooltip: {
      text: ChartTypeTooltip[ChartType.Bar],
      'image-src': barSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `bar`,
  },
  [ChartType.Column]: {
    icon: `column`,
    label: ChartTypeDisplayName[ChartType.Column],
    value: ChartType.Column,
    tooltip: {
      text: ChartTypeTooltip[ChartType.Column],
      'image-src': columnSVG,
      ...commonTooltipAttrs,
    },
    chartGroup: `series`,
  },
  [ChartType.StackedColumn]: {
    icon: `column-stacked`,
    label: ChartTypeDisplayName[ChartType.StackedColumn],
    value: ChartType.StackedColumn,
    tooltip: {
      text: ChartTypeTooltip[ChartType.StackedColumn],
      'image-src': stackedColumnSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `series`,
  },
  [ChartType.Line]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.Line],
    value: ChartType.Line,
    tooltip: {
      text: ChartTypeTooltip[ChartType.Line],
      'image-src': lineSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `series`,
  },
  [ChartType.Pie]: {
    icon: `pie`,
    label: ChartTypeDisplayName[ChartType.Pie],
    value: ChartType.Pie,
    tooltip: {
      text: ChartTypeTooltip[ChartType.Pie],
      'image-src': pieSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `standard`,
  },
  [ChartType.Metric]: {
    icon: `single-metric`,
    label: ChartTypeDisplayName[ChartType.Metric],
    value: ChartType.Metric,
    tooltip: {
      text: ChartTypeTooltip[ChartType.Metric],
      'image-src': metricSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `standard`,
  },
  [ChartType.StackedBar]: {
    icon: `bar-stacked`,
    label: ChartTypeDisplayName[ChartType.StackedBar],
    value: ChartType.StackedBar,
    tooltip: {
      text: ChartTypeTooltip[ChartType.StackedBar],
      'image-src': stackedBarSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `bar`,
  },
  [ChartType.StackedLine]: {
    icon: `stacked-line`,
    label: ChartTypeDisplayName[ChartType.StackedLine],
    value: ChartType.StackedLine,
    tooltip: {
      text: ChartTypeTooltip[ChartType.StackedLine],
      'image-src': stackedLineSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `series`,
  },
  [ChartType.Sankey]: {
    icon: `top-paths`,
    label: ChartTypeDisplayName[ChartType.Sankey],
    value: ChartType.Sankey,
    chartGroup: `special`,
  },
  [ChartType.Table]: {
    icon: `table`,
    label: ChartTypeDisplayName[ChartType.Table],
    value: ChartType.Table,
    tooltip: {
      text: ChartTypeTooltip[ChartType.Table],
      'image-src': tableSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `standard`,
  },
  [ChartType.FunnelSteps]: {
    icon: `funnel-steps`,
    label: ChartTypeDisplayName[ChartType.FunnelSteps],
    value: ChartType.FunnelSteps,
    tooltip: {
      text: ChartTypeTooltip[ChartType.FunnelSteps],
      'image-src': funnelStepsSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelTrend]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.FunnelTrend],
    value: ChartType.FunnelTrend,
    tooltip: {
      text: ChartTypeTooltip[ChartType.FunnelTrend],
      'image-src': funnelTrendsSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelFrequencyLine]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.FunnelFrequencyLine],
    value: ChartType.FunnelFrequencyLine,
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelFrequencyBar]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.FunnelFrequencyBar],
    value: ChartType.FunnelFrequencyBar,
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelTTCBar]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.FunnelTTCBar],
    value: ChartType.FunnelTTCBar,
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelTTCLine]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.FunnelTTCLine],
    value: ChartType.FunnelTTCLine,
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.FunnelMedianTTC]: {
    icon: `bar-stacked`,
    label: ChartTypeDisplayName[ChartType.FunnelMedianTTC],
    value: ChartType.FunnelMedianTTC,
    tooltip: {
      text: ChartTypeTooltip[ChartType.FunnelMedianTTC],
      'image-src': funnelTtcSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.RetentionLine]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.RetentionLine],
    value: ChartType.RetentionLine,
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.RetentionTrend]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.RetentionTrend],
    value: ChartType.RetentionTrend,
    tooltip: {
      text: ChartTypeTooltip[ChartType.RetentionTrend],
      'image-src': retentionTrendsSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  // TODO: remove
  [ChartType.RetentionTrendMetric]: {
    icon: `single-metric`,
    // use InsightMetric here, we will eventually use insight API so this is correct
    label: ChartTypeDisplayName[ChartType.Metric],
    value: ChartType.RetentionTrendMetric,
    tooltip: {
      text: ChartTypeTooltip[ChartType.RetentionTrend],
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.RetentionTable]: {
    icon: `retention-curve`,
    label: ChartTypeDisplayName[ChartType.RetentionTable],
    value: ChartType.RetentionTable,
    tooltip: {
      text: ChartTypeTooltip[ChartType.RetentionTable],
      'image-src': retentionTableSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.RetentionCurve]: {
    icon: `retention-curve`,
    label: ChartTypeDisplayName[ChartType.RetentionTable],
    value: ChartType.RetentionCurve,
    tooltip: {
      text: ChartTypeTooltip[ChartType.RetentionTable],
      'image-src': retentionTableSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.Frequency]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.Frequency],
    value: ChartType.Frequency,
    tooltip: {
      text: ChartTypeTooltip[ChartType.Frequency],
      'image-src': funnelFrequencySvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.FunnelTopPaths]: {
    icon: `top-paths`,
    label: ChartTypeDisplayName[ChartType.FunnelTopPaths],
    value: ChartType.FunnelTopPaths,
    tooltip: {
      text: ChartTypeTooltip[ChartType.FunnelTopPaths],
      'image-src': sankeySvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.FlowsTopPaths]: {
    icon: `funnel-steps`,
    label: ChartTypeDisplayName[ChartType.FlowsTopPaths],
    value: ChartType.FlowsTopPaths,
    tooltip: {
      text: ChartTypeTooltip[ChartType.FlowsTopPaths],
      'image-src': funnelStepsSvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.Flows]: {
    icon: `top-paths`,
    label: ChartTypeDisplayName[ChartType.Flows],
    value: ChartType.Sankey, // Alias
    tooltip: {
      text: ChartTypeTooltip[ChartType.Flows],
      'image-src': sankeySvg,
      ...commonTooltipAttrs,
    },
    chartGroup: `special`,
  },
  [ChartType.ImpactAdoption]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.ImpactAdoption],
    value: ChartType.ImpactAdoption,
    chartGroup: `special`,
  },
  [ChartType.ImpactTrends]: {
    icon: `stacked-line`,
    label: ChartTypeDisplayName[ChartType.ImpactTrends],
    value: ChartType.ImpactTrends,
    chartGroup: `special`,
  },
  [ChartType.ImpactPropensity]: {
    icon: `bar-stacked`,
    label: ChartTypeDisplayName[ChartType.ImpactPropensity],
    value: ChartType.ImpactPropensity,
    chartGroup: `special`,
  },
};
